// Пользователь внизу сайдбара (задача D5, ui-spec §2): «ВТ Влад Т. · Админ» и меню «⋯»: тема (Система / Светлая /
// Тёмная), «Клавиши» (шпаргалка «?» — её же открывает меню, если клавиша не под рукой), «Как пользоваться пультом»,
// «Выйти». В сайдбаре иконками — только кружок с инициалами, он же открывает меню.
import { ROLE_RU } from '@contract/index.js';
import { BookOpen, Ellipsis, Keyboard, LogOut } from 'lucide-react';
import { Link } from 'react-router';
import { messageRuOf } from '@/api/errors';
import { useLogout, useMe } from '@/api/queries/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuLinkItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { setThemePref, THEME_PREFS, THEME_RU, useThemePref, type ThemePref } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { initialsOf } from './nav';
import { toastError } from './toaster';

function isThemePref(value: unknown): value is ThemePref {
  return typeof value === 'string' && (THEME_PREFS as readonly string[]).includes(value);
}

function Avatar({ name }: { readonly name: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-grid size-6 shrink-0 place-items-center rounded-full bg-muted text-sm font-semibold text-foreground"
    >
      {initialsOf(name)}
    </span>
  );
}

export function UserMenu({ compact = false, onOpenShortcuts }: { readonly compact?: boolean; readonly onOpenShortcuts: () => void }) {
  const me = useMe();
  const theme = useThemePref();
  const logout = useLogout();
  if (me === undefined) return null;
  const caption = `${me.user.shortName} · ${ROLE_RU[me.user.role]}`;

  const menu = (
    <DropdownMenuContent side={compact ? 'right' : 'top'} align={compact ? 'end' : 'start'} className="w-60">
      <DropdownMenuGroup>
        <DropdownMenuLabel>Тема</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value: unknown) => {
            if (isThemePref(value)) setThemePref(value);
          }}
        >
          {THEME_PREFS.map((pref) => (
            <DropdownMenuRadioItem key={pref} value={pref}>
              {THEME_RU[pref]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={onOpenShortcuts}>
        <Keyboard aria-hidden="true" />
        Клавиши
        <DropdownMenuShortcut>?</DropdownMenuShortcut>
      </DropdownMenuItem>
      <DropdownMenuLinkItem render={<Link to="/help/guide-console" />}>
        <BookOpen aria-hidden="true" />
        Как пользоваться пультом
      </DropdownMenuLinkItem>
      <DropdownMenuItem
        disabled={logout.isPending}
        onClick={() => {
          logout.mutate(undefined, {
            onError: (error) => {
              toastError(`Выйти не удалось: ${messageRuOf(error)}`);
            },
          });
        }}
      >
        <LogOut aria-hidden="true" />
        Выйти
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={`${caption} — меню`}
          title={caption}
          className="btn mx-auto flex size-8 cursor-pointer items-center justify-center rounded-md hover:bg-muted aria-expanded:bg-muted"
        >
          <Avatar name={me.user.name} />
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2 px-2">
      <Avatar name={me.user.name} />
      <span className={cn('min-w-0 flex-1 truncate text-sm')}>{caption}</span>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon-sm" aria-label="Меню пользователя" className="max-sm:size-10" />}
        >
          <Ellipsis aria-hidden="true" className="text-muted-foreground" />
        </DropdownMenuTrigger>
        {menu}
      </DropdownMenu>
    </div>
  );
}
